/* ═══════════════════════════════════════════════════════════
   DARF — Cart: drawer, AJAX add/change, product card ATCs
   Depends on: darf-utils.js
   Exposes: window.DarfCart
   ═══════════════════════════════════════════════════════════ */

(function () {
  'use strict';

  var u = window.DarfUtils;
  if (!u) return;

  var $ = u.$, $$ = u.$$, on = u.on, el = u.el, money = u.money;

  /* ── Drawer elements ────────────────────────────────────── */
  var cartDrawer  = document.getElementById('cart-drawer');
  var cartOverlay = document.getElementById('cart-drawer-overlay') || document.querySelector('.cart-drawer__overlay');
  var cartClose   = document.getElementById('cart-close') || document.querySelector('[data-cart-close]');
  var cartBody    = document.getElementById('cart-drawer-body');
  var cartFooter  = document.getElementById('cart-drawer-footer');

  /* Focus trap for cart drawer */
  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  var _prevFocus = null;
  function trapHandler(e) {
    if (e.key !== 'Tab' || !cartDrawer || !cartDrawer.classList.contains('is-open')) return;
    var nodes = Array.from(cartDrawer.querySelectorAll(FOCUSABLE)).filter(function(n){ return !n.hasAttribute('hidden') && n.offsetParent !== null; });
    if (!nodes.length) return;
    var first = nodes[0], last = nodes[nodes.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function openCart()  {
    cartDrawer  && cartDrawer.classList.add('is-open');
    cartOverlay && cartOverlay.classList.add('is-open');
    document.body.style.overflow = 'hidden';
    if (cartDrawer) {
      cartDrawer.setAttribute('aria-modal', 'true');
      cartDrawer.setAttribute('role', 'dialog');
    }
    _prevFocus = document.activeElement;
    document.addEventListener('keydown', trapHandler);
    setTimeout(function(){
      if (cartDrawer) {
        var first = cartDrawer.querySelector(FOCUSABLE);
        if (first) try { first.focus(); } catch(_){}
      }
    }, 80);
    refreshCart();
  }
  function closeCart() {
    cartDrawer  && cartDrawer.classList.remove('is-open');
    cartOverlay && cartOverlay.classList.remove('is-open');
    document.body.style.overflow = '';
    if (cartDrawer) cartDrawer.removeAttribute('aria-modal');
    document.removeEventListener('keydown', trapHandler);
    if (_prevFocus && _prevFocus.focus) { try { _prevFocus.focus(); } catch(_){} _prevFocus = null; }
  }

  on(cartClose, 'click', closeCart);
  on(cartOverlay, 'click', function (e) {
    if (e.target === cartOverlay || e.target.dataset.cartClose !== undefined) closeCart();
  });
  $$('[data-open-cart]').forEach(function (btn) { on(btn, 'click', openCart); });

  /* ── Shopify Cart API ───────────────────────────────────── */
  function fetchCart() {
    return fetch('/cart.js').then(function (r) { return r.json(); });
  }

  function addToCart(variantId, qty) {
    qty = qty || 1;
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: qty })
    })
    .then(function (r) { return r.json(); })
    .then(function (data) {
      /* Shopify antwortet auf einen ausverkauften Artikel mit 422 und einem
         fertigen deutschen Satz ("... ist bereits ausverkauft"). Der wird
         weitergereicht statt weggeworfen: bis zum 28.09.2026 landete jeder
         dieser Faelle im Toast "Fehler, bitte erneut versuchen", also in der
         Aufforderung, etwas zu wiederholen, das nie klappen kann. */
      if (data.status) {
        var err = new Error(data.description || data.message || 'Error adding to cart');
        err.shopifyMessage = data.description || data.message || '';
        throw err;
      }
      return refreshCart().then(function () { openCart(); return data; });
    });
  }

  function changeCartItem(key, qty) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    })
    .then(function (r) { return r.json(); })
    .then(function (data) { renderCart(data); return data; });
  }

  function refreshCart() {
    return fetchCart().then(function (cart) {
      renderCart(cart);
      updateCartCount(cart.item_count);
      return cart;
    });
  }

  function updateCartCount(count) {
    $$('[data-cart-count]').forEach(function (node) {
      node.textContent = count;
      node.style.display = count > 0 ? 'flex' : 'none';
    });
  }

  /* ── Cart render (safe DOM — no innerHTML for user data) ── */
  function buildCartItem(item) {
    var wrap = el('div', { class: 'cart-item', data: { key: item.key } });

    var imgWrap = el('div', { class: 'cart-item__img' });
    if (item.image) {
      var img = document.createElement('img');
      img.src = item.image; img.alt = ''; img.loading = 'lazy';
      imgWrap.appendChild(img);
    } else {
      imgWrap.innerHTML = '<svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3.5 7.5 12 3l8.5 4.5v9L12 21l-8.5-4.5z"/><path d="M3.5 7.5 12 12l8.5-4.5"/><path d="M12 21v-9"/></svg>';
    }
    wrap.appendChild(imgWrap);

    var info = el('div', { class: 'cart-item__info' });

    var vendor = el('div', { class: 'cart-item__vendor' });
    vendor.textContent = item.vendor;
    info.appendChild(vendor);

    var name = el('div', { class: 'cart-item__name' });
    name.textContent = item.product_title;
    info.appendChild(name);

    if (item.variant_title && item.variant_title !== 'Default Title') {
      var variant = el('div', { class: 'cart-item__variant' });
      variant.textContent = item.variant_title;
      info.appendChild(variant);
    }

    var footer = el('div', { class: 'cart-item__footer' });

    var priceWrap = el('div', { class: 'cart-item__price-wrap' });
    var price = el('div', { class: 'cart-item__price' });
    price.textContent = money(item.final_line_price);
    priceWrap.appendChild(price);
    if (item.grams && item.grams > 0) {
      var totalGrams = item.grams * item.quantity;
      var perKg = el('div', { class: 'cart-item__per-kg' });
      perKg.textContent = '(' + money(item.final_line_price / (totalGrams / 1000)) + '/kg)';
      priceWrap.appendChild(perKg);
    }
    footer.appendChild(priceWrap);

    var qtyWrap = el('div', { class: 'qty-wrap' });
    var minus = el('button', { class: 'qty-btn qty-btn--minus', type: 'button' });
    minus.textContent = '−';
    var num = el('div', { class: 'qty-num' });
    num.textContent = String(item.quantity);
    var plus = el('button', { class: 'qty-btn qty-btn--plus', type: 'button' });
    plus.textContent = '+';
    qtyWrap.append(minus, num, plus);
    footer.appendChild(qtyWrap);

    info.appendChild(footer);
    wrap.appendChild(info);

    on(minus, 'click', function () { changeCartItem(item.key, item.quantity - 1); });
    on(plus,  'click', function () { changeCartItem(item.key, item.quantity + 1); });

    return wrap;
  }

  function renderCart(cart) {
    if (!cartBody) return;
    cartBody.textContent = '';
    if (cartFooter) { cartFooter.textContent = ''; cartFooter.style.display = 'none'; }

    if (!cart.item_count) {
      var empty = el('div', { class: 'cart-empty' });
      var icon  = el('div', { class: 'cart-empty__icon' });
      icon.innerHTML = '<svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="9" cy="20" r="1.4"/><circle cx="17" cy="20" r="1.4"/><path d="M3 4h2l2.3 11a1 1 0 0 0 1 .8h8.3a1 1 0 0 0 1-.8L20 7H6"/></svg>';
      var msg   = el('p');
      msg.style.fontWeight = '600';
      msg.textContent = 'Ihr Warenkorb ist leer';
      var cta   = el('a', { class: 'btn btn-outline btn-sm', href: '/' });
      cta.textContent = 'Weiter einkaufen';
      empty.append(icon, msg, cta);
      cartBody.appendChild(empty);
      return;
    }

    cart.items.forEach(function (item) { cartBody.appendChild(buildCartItem(item)); });

    if (cartFooter) {
      cartFooter.style.display = '';
      var totalRow = el('div', { class: 'cart-drawer__total-row' });
      var lbl = el('span');
      lbl.textContent = 'Zwischensumme';
      var val = el('span');
      val.textContent = money(cart.total_price);
      totalRow.append(lbl, val);

      var note = el('p', { class: 'cart-drawer__note' });
      note.textContent = 'Steuern und Versandkosten werden an der Kasse berechnet.';

      var checkout = el('a', { class: 'btn btn-fill btn-full', href: '/checkout' });
      checkout.textContent = 'Zur Kasse →';

      var viewCart = el('a', { class: 'btn btn-ghost btn-full', href: '/cart' });
      viewCart.style.marginTop = '8px';
      viewCart.textContent = 'Warenkorb anzeigen';
      viewCart.style.color = 'var(--muted)';
      viewCart.style.borderColor = 'rgba(37,90,52,.2)';

      cartFooter.append(totalRow, note, checkout, viewCart);
    }
  }

  /* ── Product card ATC buttons ───────────────────────────── */
  on(document, 'click', function (e) {
    var btn = e.target.closest('[data-atc-btn]');
    if (!btn) return;
    e.preventDefault();
    var variantId = parseInt(btn.dataset.variantId, 10);
    if (!variantId) return;
    btn.disabled = true;
    addToCart(variantId, 1)
      .then(function () { if (window.DarfToast) DarfToast('✓ In den Warenkorb!'); })
      .catch(function (err) {
        var text = (err && err.shopifyMessage) || 'Fehler, bitte erneut versuchen';
        if (window.DarfToast) DarfToast(text);
      })
      .finally(function () { btn.disabled = false; });
  });

  /* ── Cart page qty buttons ──────────────────────────────── */
  /* changeCartItem aendert den Warenkorb und zeichnet danach die SCHUBLADE neu.
     Auf der Warenkorbseite ist die Schublade aber gar nicht sichtbar: die Menge
     wurde also geaendert und die Seite zeigte weiter die alte Zahl. Von aussen
     sah das aus, als liesse sich die Menge nicht aendern.
     Auf /cart wird deshalb neu geladen, denn die Zeilen dort rendert Liquid,
     nicht dieses Skript. */
  on(document, 'click', function (e) {
    var btn = e.target.closest('[data-cart-qty-btn]');
    if (!btn) return;
    var qty = parseInt(btn.dataset.qty, 10);
    var onCartPage = window.location.pathname.replace(/\/$/, '') === '/cart';
    if (onCartPage) {
      // Doppelklicks zaehlen sonst zweimal, waehrend die Seite schon neu laedt.
      $$('[data-cart-qty-btn]').forEach(function (b) { b.disabled = true; });
    }
    changeCartItem(btn.dataset.key, qty)
      .then(function () { if (onCartPage) window.location.reload(); })
      .catch(function () {
        if (onCartPage) $$('[data-cart-qty-btn]').forEach(function (b) { b.disabled = false; });
        if (window.DarfToast) DarfToast('Menge konnte nicht geändert werden');
      });
  });

  /* ── Anmerkung zur Bestellung ───────────────────────────── */
  /* Das Feld liegt im Warenkorbformular und wandert beim Absenden mit. Wer aber
     aus der Schublade heraus zur Kasse geht, sendet dieses Formular nie, und die
     Anmerkung waere verloren. Deshalb wird sie beim Verlassen des Feldes
     gespeichert. */
  /* Was bei einer Nachbestellung weggefallen ist. Der Satz entsteht auf der
     Kontoseite und wird hier gezeigt, weil der Kunde inzwischen woanders steht:
     ein Toast auf der alten Seite waere weg, bevor er gelesen ist. */
  try {
    var weg = sessionStorage.getItem('darf:reorder-weg');
    if (weg) {
      sessionStorage.removeItem('darf:reorder-weg');
      if (window.DarfToast) DarfToast('Nicht mehr lieferbar und daher nicht im Warenkorb: ' + weg);
    }
  } catch (e) { /* ohne sessionStorage einfach ohne Hinweis */ }

  var noteField = document.querySelector('[data-cart-note]');
  if (noteField) {
    var lastSaved = noteField.value;
    var hint = document.querySelector('.cart-note__hint');
    var hintText = hint ? hint.textContent : '';
    on(noteField, 'change', function () {
      var value = noteField.value;
      if (value === lastSaved) return;
      fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: value })
      })
      .then(function (r) { if (!r.ok) throw new Error('save failed'); return r.json(); })
      .then(function () {
        lastSaved = value;
        if (!hint) return;
        hint.textContent = 'Anmerkung gespeichert.';
        hint.classList.add('is-saved');
        clearTimeout(hint._timer);
        hint._timer = setTimeout(function () {
          hint.textContent = hintText;
          hint.classList.remove('is-saved');
        }, 2500);
      })
      .catch(function () {
        // Kein Drama: das Formular traegt die Anmerkung beim Absenden ohnehin mit.
        if (window.DarfToast) DarfToast('Anmerkung wird beim Absenden übernommen');
      });
    });
  }

  /* ── Init ───────────────────────────────────────────────── */
  fetchCart().then(function (cart) { updateCartCount(cart.item_count); }).catch(function () {});

  /* ── Public API ─────────────────────────────────────────── */
  window.DarfCart = { add: addToCart, refresh: refreshCart, open: openCart, close: closeCart };
})();
